import { Link, createFileRoute } from '@tanstack/react-router'
import { ArrowLeft, Loader2 } from 'lucide-react'
import { CourseScaffold } from './-components/CourseScaffold'
import { FavoriteButton } from './-components/FavoriteButton'
import { PageTitle } from '@/components/PageTitle'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import { useLearnerCourse } from '@/hooks/repositories/useLearningRepository'
import { formatDate } from '@/lib/formatters'

export const Route = createFileRoute('/_app/learning/$courseSlug/')({
  component: RouteComponent,
})

/**
 * Course intro page (sc-1245).
 *
 * The page someone reads before deciding to start, and returns to between
 * lessons to see where they are. Addressed by slug, which is what makes it
 * structurally impossible to land on an unpublished draft from here.
 *
 * The layout lives in CourseScaffold, shared with the author preview.
 */
function RouteComponent() {
  const { courseSlug } = Route.useParams()
  const { data: course, isPending, isError } = useLearnerCourse(courseSlug)

  if (isPending) {
    return (
      <div className="flex items-center justify-center py-24">
        <Loader2 className="size-5 animate-spin text-muted-foreground" />
      </div>
    )
  }

  if (isError) {
    return (
      <div className="py-4">
        <PageTitle title="Learning" />
        <Card>
          <CardContent className="flex flex-col items-center gap-3 py-16 text-center">
            <p className="text-sm font-medium">Course not found</p>
            <p className="text-sm text-muted-foreground">
              It may not be published yet.
            </p>
            <Button asChild variant="outline" size="sm" className="mt-2">
              <Link to="/learning">Back to Learning</Link>
            </Button>
          </CardContent>
        </Card>
      </div>
    )
  }

  const completedCount = course.lessons.filter(
    (lesson) => lesson.completedAt,
  ).length
  const nextLesson = course.lessons.find((lesson) => !lesson.completedAt)

  return (
    <div className="py-4">
      <PageTitle title={`Learning — ${course.title}`} />

      <Button asChild variant="ghost" size="sm" className="mb-3 -ml-2">
        <Link to="/learning">
          <ArrowLeft className="mr-1 size-4" aria-hidden />
          Learning
        </Link>
      </Button>

      <CourseScaffold
        heroImageAssetId={course.heroImageAssetId}
        title={course.title}
        summary={course.summary}
        agendaMarkdown={course.agendaMarkdown}
        introVideoAssetId={course.introVideoAssetId}
        introExternalUrl={course.introExternalUrl}
        lessons={course.lessons}
        chapters={course.chapters}
        headerActions={
          <FavoriteButton
            courseId={course.courseId}
            courseSlug={courseSlug}
            isFavorite={course.isFavorite}
          />
        }
        attachments={course.attachments}
        totalMinutes={course.estimatedMinutes}
        completedCount={completedCount}
        meta={course.dueAt && <span>Due {formatDate(course.dueAt)}</span>}
        progressNote={
          !course.assignmentId && (
            <p className="text-xs text-muted-foreground">
              Not assigned to you — you can read it any time.
            </p>
          )
        }
        primaryAction={
          nextLesson ? (
            <Button asChild>
              <Link
                to="/learning/$courseSlug/lesson/$lessonId"
                params={{ courseSlug, lessonId: nextLesson.id }}
              >
                {completedCount > 0 ? 'Continue' : 'Start course'}
              </Link>
            </Button>
          ) : (
            <Button disabled>Course complete</Button>
          )
        }
        renderLesson={(lesson, _index, content) => (
          <Link
            to="/learning/$courseSlug/lesson/$lessonId"
            params={{ courseSlug, lessonId: lesson.id }}
            className="flex items-start gap-3 rounded-lg border bg-card p-4 transition-colors hover:bg-muted/50"
          >
            {content}
          </Link>
        )}
      />
    </div>
  )
}
